/* iter105 — Outils : sauvegarde, restauration, réinitialisation (admin). */
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wrench, DownloadSimple, UploadSimple, ArrowCounterClockwise, Database, Warning, ClockCounterClockwise, Trash, ListBullets } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const CHUNK = 512 * 1024;
const fmtBytes = (n) => n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.round(n / 1024)} Ko`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';

export default function Tools() {
  const { logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState('');
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState('');
  const [confirmReset, setConfirmReset] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const fileRef = useRef(null);
  const [backups, setBackups] = useState({ items: [], retention_days: 30 });
  const [audit, setAudit] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [confirmAuto, setConfirmAuto] = useState('');

  const loadBackups = () => api.get('/tools/backups').then(r => setBackups(r.data)).catch(() => {});
  const loadAudit = () => api.get('/tools/audit', { params: { limit: 100 } }).then(r => setAudit(r.data.items || [])).catch(() => {});
  const refreshAll = () => { load(); loadBackups(); loadAudit(); };

  const runAuto = async () => {
    setBusy('auto');
    try { const { data } = await api.post('/tools/backups/run'); toast.success(`Sauvegarde ${data.file} créée (${data.documents} documents)`); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };
  const downloadAuto = async (name) => {
    try {
      const res = await api.get(`/tools/backups/${name}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const deleteAuto = async (name) => {
    try { await api.delete(`/tools/backups/${name}`); toast.success('Sauvegarde supprimée'); loadBackups(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const restoreAuto = async () => {
    if (!restoreTarget || confirmAuto !== 'RESTAURER') return;
    setBusy('restore-auto');
    try {
      const { data } = await api.post(`/tools/backups/${restoreTarget.name}/restore`, { confirm: 'RESTAURER' });
      toast.success(`Restauration du ${restoreTarget.date} terminée : ${data.documents} documents. Reconnexion requise.`);
      setRestoreTarget(null); setConfirmAuto('');
      setTimeout(async () => { await logout(); window.location.href = '/login'; }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de restauration'); } finally { setBusy(''); }
  };

  const load = () => api.get('/tools/overview').then(r => setOverview(r.data)).catch(() => {});
  useEffect(() => { load(); loadBackups(); loadAudit(); }, []);

  const backup = async () => {
    setBusy('backup');
    try {
      const res = await api.get('/tools/backup', { responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename=([^;]+)/)?.[1] || 'sauvegarde.json.gz';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Sauvegarde téléchargée (${res.headers['x-backup-documents'] || '?'} documents)`);
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde'); }
    finally { setBusy(''); }
  };

  const restore = async () => {
    if (!file || confirmRestore !== 'RESTAURER') return;
    setBusy('restore'); setProgress(0);
    try {
      const uploadId = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.post(`/tools/restore/chunk?upload_id=${uploadId}&index=${i}`, blob, { headers: { 'Content-Type': 'application/octet-stream' } });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await api.post('/tools/restore/commit', { upload_id: uploadId, confirm: 'RESTAURER' });
      setProgress(100);
      toast.success(`Restauration terminée : ${data.documents} documents, ${data.collections} collections. Reconnexion requise.`);
      setTimeout(async () => { await logout(); window.location.href = '/login'; }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de restauration'); }
    finally { setBusy(''); }
  };

  const reset = async () => {
    if (confirmReset !== 'REINITIALISER') return;
    setBusy('reset');
    try {
      const { data } = await api.post('/tools/reset', { confirm: 'REINITIALISER' });
      toast.success(data.message);
      setResetOpen(false); setConfirmReset('');
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de réinitialisation'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-6" data-testid="tools-page">
      <div>
        <h1 className="text-2xl font-outfit font-bold text-[#0F172A] flex items-center gap-2"><Wrench size={26} weight="duotone" className="text-[#0052FF]" /> Outils</h1>
        <p className="text-sm text-[#64748B]">Sauvegarde, restauration et réinitialisation complètes de la base de données.</p>
      </div>

      {overview && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="tools-overview">
          <CardContent className="p-4 flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2"><Database size={18} className="text-[#0052FF]" /><span><b data-testid="tools-collections">{overview.collections}</b> collections</span></div>
            <div><b data-testid="tools-documents">{overview.documents.toLocaleString('fr-FR')}</b> documents</div>
            <div className="text-[#64748B]">Base : <code>{overview.db_name}</code></div>
            {overview.last_action && (
              <div className="ml-auto text-xs text-[#64748B]" data-testid="tools-last-action">
                Dernière action : <b>{overview.last_action.action}</b> par {overview.last_action.by} le {fmtDate(overview.last_action.created_at)}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="tools-backup-card">
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center gap-2"><DownloadSimple size={18} className="text-emerald-600" /> Sauvegarde</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <p>Exporte <b>toutes les données</b> (élèves, paiements, personnel, paramètres, comptes…) dans un fichier <code>.json.gz</code> téléchargeable.</p>
            <Button onClick={backup} disabled={busy !== ''} data-testid="tools-backup-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">
              <DownloadSimple size={16} className="mr-1.5" />{busy === 'backup' ? 'Préparation…' : 'Sauvegarder maintenant'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] shadow-none" data-testid="tools-restore-card">
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center gap-2"><UploadSimple size={18} className="text-[#0052FF]" /> Restauration</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <p>Remplace <b>l'intégralité</b> de la base par le contenu d'un fichier de sauvegarde. Les données actuelles seront écrasées.</p>
            <input ref={fileRef} type="file" accept=".gz,.json,application/gzip" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="tools-restore-file"
              className="block w-full text-xs file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5" />
            {file && <div className="text-xs text-[#64748B]" data-testid="tools-restore-filename">{file.name} · {fmtBytes(file.size)}</div>}
            <div className="space-y-1">
              <Label className="text-xs">Tapez <b>RESTAURER</b> pour confirmer</Label>
              <Input value={confirmRestore} onChange={e => setConfirmRestore(e.target.value.toUpperCase())} data-testid="tools-restore-confirm" placeholder="RESTAURER" />
            </div>
            {busy === 'restore' && <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-[#0052FF] transition-all" style={{ width: `${progress}%` }} data-testid="tools-restore-progress" /></div>}
            <Button onClick={restore} disabled={busy !== '' || !file || confirmRestore !== 'RESTAURER'} data-testid="tools-restore-btn" className="bg-[#0052FF] hover:bg-[#0041CC] text-white w-full">
              <UploadSimple size={16} className="mr-1.5" />{busy === 'restore' ? `Restauration ${progress}%` : 'Restaurer depuis le fichier'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-red-200 shadow-none bg-red-50/30" data-testid="tools-reset-card">
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center gap-2 text-red-700"><ArrowCounterClockwise size={18} /> Réinitialisation</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <p>Efface <b>toutes les données</b> et recrée une base initiale saine (configuration par défaut). Seul <b>votre compte administrateur</b> est conservé.</p>
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2"><Warning size={16} className="shrink-0" /> Action irréversible. Faites une sauvegarde avant.</div>
            <Button variant="outline" onClick={() => setResetOpen(true)} disabled={busy !== ''} data-testid="tools-reset-open" className="border-red-300 text-red-700 hover:bg-red-50 w-full">
              <ArrowCounterClockwise size={16} className="mr-1.5" /> Réinitialiser la base
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="tools-auto-backups-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-[#0052FF]" /> Sauvegardes automatiques (nocturnes, {backups.retention_days} jours)</span>
            <Button size="sm" variant="outline" onClick={runAuto} disabled={busy !== ''} data-testid="tools-auto-run"><Database size={14} className="mr-1.5" />{busy === 'auto' ? 'En cours…' : 'Sauvegarder maintenant (serveur)'}</Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-xs text-[#64748B] mb-2">Une sauvegarde complète est créée chaque nuit à 02h30 UTC et conservée {backups.retention_days} jours. Restaurez n'importe quel jour en un clic.</p>
          {backups.items.length === 0 && <div className="text-[#94A3B8]" data-testid="tools-auto-empty">Aucune sauvegarde automatique pour le moment.</div>}
          {backups.items.length > 0 && (
            <div className="rounded-md border border-[#E2E8F0] overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Fichier</TableHead><TableHead>Taille</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {backups.items.map(b => (
                    <TableRow key={b.name} data-testid={`tools-auto-row-${b.date}`}>
                      <TableCell className="font-medium">{new Date(b.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                      <TableCell className="text-xs text-[#64748B] font-mono">{b.name}</TableCell>
                      <TableCell className="text-xs">{fmtBytes(b.size)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => downloadAuto(b.name)} data-testid={`tools-auto-download-${b.date}`}><DownloadSimple size={14} /></Button>
                        <Button size="sm" onClick={() => { setRestoreTarget(b); setConfirmAuto(''); }} data-testid={`tools-auto-restore-${b.date}`} className="bg-[#0052FF] hover:bg-[#0041CC] text-white"><UploadSimple size={14} className="mr-1" /> Restaurer</Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteAuto(b.name)} data-testid={`tools-auto-delete-${b.date}`}><Trash size={14} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none" data-testid="tools-audit-card">
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-base flex items-center gap-2"><ListBullets size={18} className="text-[#0052FF]" /> Journal des outils</CardTitle></CardHeader>
        <CardContent className="text-sm">
          {audit.length === 0 && <div className="text-[#94A3B8]" data-testid="tools-audit-empty">Aucune action enregistrée.</div>}
          {audit.length > 0 && (
            <div className="rounded-md border border-[#E2E8F0] overflow-x-auto max-h-80 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Action</TableHead><TableHead>Par</TableHead><TableHead>Détails</TableHead></TableRow></TableHeader>
                <TableBody>
                  {audit.map(a => (
                    <TableRow key={a.id} data-testid={`tools-audit-row-${a.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                      <TableCell>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${a.action === 'reset' ? 'bg-red-50 text-red-700' : a.action === 'restore' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {{ backup: 'Sauvegarde', auto_backup: 'Sauvegarde auto', restore: 'Restauration', reset: 'Réinitialisation', regularize_returning: 'Régularisation réinscrits', expense_create: 'Dépense créée', expense_update: 'Dépense modifiée', expense_delete: 'Dépense supprimée' }[a.action] || a.action}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{a.by}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">
                        {a.action === 'regularize_returning' && `${a.updated ?? 0} élève(s) régularisé(s)${a.active_year ? ` · ${a.active_year}` : ''}`}
                        {['expense_create', 'expense_update', 'expense_delete'].includes(a.action) && `${a.poste_label || a.expense_type || ''}${a.designation ? ` · ${a.designation}` : ''}${a.amount != null ? ` · ${new Intl.NumberFormat('fr-FR').format(a.amount)} FCFA` : ''}`}
                        {a.documents != null && `${a.documents} documents`}{a.collections != null && ` · ${a.collections} collections`}
                        {a.file && ` · ${a.file}`}{a.source && ` · depuis ${a.source}`}{a.size != null && ` · ${fmtBytes(a.size)}`}
                        {a.backup_created_at && ` · sauvegarde du ${fmtDate(a.backup_created_at)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}>
        <DialogContent data-testid="tools-auto-restore-dialog">
          <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><UploadSimple size={20} className="text-[#0052FF]" /> Restaurer la sauvegarde du {restoreTarget?.date}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Les données actuelles seront <b>remplacées</b> par l'état du {restoreTarget?.date}. Tapez <b>RESTAURER</b> pour confirmer.</p>
          <Input value={confirmAuto} onChange={e => setConfirmAuto(e.target.value.toUpperCase())} placeholder="RESTAURER" data-testid="tools-auto-restore-confirm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} data-testid="tools-auto-restore-cancel">Annuler</Button>
            <Button onClick={restoreAuto} disabled={confirmAuto !== 'RESTAURER' || busy === 'restore-auto'} data-testid="tools-auto-restore-btn" className="bg-[#0052FF] hover:bg-[#0041CC] text-white">
              {busy === 'restore-auto' ? 'Restauration…' : 'Restaurer ce jour'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="tools-reset-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-red-700 flex items-center gap-2"><Warning size={20} /> Confirmer la réinitialisation</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Toutes les données (élèves, paiements, personnel, historique…) seront définitivement supprimées. Tapez <b>REINITIALISER</b> pour continuer.</p>
          <Input value={confirmReset} onChange={e => setConfirmReset(e.target.value.toUpperCase())} placeholder="REINITIALISER" data-testid="tools-reset-confirm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="tools-reset-cancel">Annuler</Button>
            <Button onClick={reset} disabled={confirmReset !== 'REINITIALISER' || busy === 'reset'} data-testid="tools-reset-btn" className="bg-red-600 hover:bg-red-700 text-white">
              {busy === 'reset' ? 'Réinitialisation…' : 'Tout effacer et réinitialiser'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
